import React from "react";
import { useLocation } from "react-router-dom";
// See app.jsx for polyfills.

import { fetchParentMenuItem, fetchOwnMenuItem } from "./Menu";

import variables from "../../../scss/variables.scss";

import audioGuideGroups from "../Components/AudioGuide/AudioGuideGroups";

/* eslint-disable import/prefer-default-export */

const { AbortController } = window;

// The API map is stored outside of the useAudioGuideApi call
// so any useAudioGuideApi call uses the same instance of the
// map, ensuring caching works for the lifetime of the page.
const audioGuideApiMap = new Map();

export const useAudioGuideApi = () => {
  const controller = new AbortController();

  const fetchByUrl = async (url) => {
    if (audioGuideApiMap.has(url)) {
      return audioGuideApiMap.get(url);
    }

    try {
      const fetchUrl = new URL(url, window.location.toString());
      if (url.indexOf('/jsonapi/') < 0) {
        fetchUrl.searchParams.set('_format', 'json');
      }
      const res = await fetch(
        fetchUrl.toString(),
        {
          headers: { accept: "application/json" },
          signal: controller.signal
        }
      );
      const json = await res.json();
      audioGuideApiMap.set(url, json);
      if ((json.nid || []).length && json.nid[0].value) {
        audioGuideApiMap.set(`/node/${json.nid[0].value}`, json);
      }
      return json;
    } catch (e) {
      if (e.name === "AbortError") {
        return null;
      }
      /* eslint-disable no-console */
      console.error(e);
      /* eslint-enable no-console */
      throw e;
    }
  };

  const abortFetch = () => controller.abort();

  return [fetchByUrl, abortFetch];
};

/**
 * Get window size.
 * @param {number} width The original window width.
 * @param {number} height The original window height.
 * @return {ISubmitResult<object>|undefined} The current window size.
 */
export function useWindowSize(width = 800, height = 600) {
  const isClient = typeof window === "object";

  function getSize() {
    return {
      width: isClient ? window.innerWidth : width,
      height: isClient ? window.innerHeight : height
    };
  }
  const vhSize = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  };

  const [windowSize, setWindowSize] = React.useState(getSize);

  React.useEffect(() => {
    if (!isClient) {
      return false;
    }
    vhSize();
    /**
     * Handle resize events.
     *
     * Since Safari will sometimes fire resize events during a scroll,
     * check to make sure that the window size has actually changed.
     */
    function handleResize() {
      const currentSize = getSize();
      vhSize();
      if (
        currentSize.width !== windowSize.width ||
        currentSize.height !== windowSize.height
      ) {
        setWindowSize(currentSize);
      }
    }

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return windowSize;
}

/**
 * Get media query value.
 * @return {ISubmitResult<string>|undefined} The current media query.
 */
export function useMediaQuery() {
  const { width } = useWindowSize();

  const baseline = parseInt(variables.baseline, 10) || 8;
  const xs = parseInt(variables["media-xs"], 10) * baseline * 2;
  const sm = parseInt(variables["media-sm"], 10) * baseline * 2;
  const md = parseInt(variables["media-md"], 10) * baseline * 2;
  const lg = parseInt(variables["media-lg"], 10) * baseline * 2;
  const xlg = parseInt(variables["media-xlg"], 10) * baseline * 2;

  let mediaQuery = "xxs";

  if (width >= xs && width < sm) {
    mediaQuery = "xs";
  } else if (width >= sm && width < md) {
    mediaQuery = "sm";
  } else if (width >= md && width < lg) {
    mediaQuery = "md";
  } else if (width >= lg && width < xlg) {
    mediaQuery = "lg";
  } else if (width >= xlg) {
    mediaQuery = "xlg";
  }

  return mediaQuery;
}

/**
 * Get device type based on width.
 * @return {ISubmitResult<string>|undefined} The current device type.
 */
export function useDevice() {
  const mediaQuery = useMediaQuery();

  let device;

  if (["xxs", "xs"].includes(mediaQuery)) {
    device = "mobile";
  } else if (["sm", "md"].includes(mediaQuery)) {
    device = "tablet";
  } else if (["lg", "xlg"].includes(mediaQuery)) {
    device = "desktop";
  }

  return device || "mobile";
}

/**
 * Detect whether the page is pinch/zoomed.
 * @return {ISubmitResult<number>|undefined} The current zoom level.
 */
export function usePinchZoomDetect() {
  const [zoom, setZoom] = React.useState(1);

  const onWheel = e => {
    if (e.ctrlKey) {
      const scale = zoom - e.deltaY * 0.01;
      setZoom(scale);
    }
  };

  React.useEffect(() => {
    window.addEventListener("wheel", onWheel, false);
    return () => window.removeEventListener("wheel", onWheel, false);
  }, []);

  return zoom;
}

/**
 * Common provider for menu data.
 * @return {ISubmitResult<object>|undefined} The current menu tree.
 */
export function useMenuData() {
  const [menuTree, setMenuTree] = React.useState([]);
  const [fetchByUrl, abortFetch] = useAudioGuideApi();

  React.useEffect(() => {
    fetchByUrl("/api/ag_nav_menu_items/main")
      .then(json => setMenuTree(json))
      .catch();
    return () => abortFetch();
  }, []);

  return menuTree;
}

/**
 * Provider for context nodes, such as the current Audio Guide.
 * @param {string} parentUrl The current parent URL. If blank, derive from current location.
 * @return {ISubmitResult<object>|undefined} The current location's context node.
 */
export function useContextualNode(parentUrl = "") {
  const location = useLocation();
  const [fetchByUrl, abortFetch] = useAudioGuideApi();
  const [contextNode, setContextNode] = React.useState(null);
  const menuTree = useMenuData();
  const parentUrlString = parentUrl.toString();

  React.useEffect(() => {
    const fetchUrl = parentUrlString.length > 0
      ? { pathname: parentUrlString }
      : location;
    const parentMenuItem =
      parentUrlString.length > 0
        ? fetchOwnMenuItem(fetchUrl, menuTree)
        : fetchParentMenuItem(fetchUrl, menuTree);
    if (parentMenuItem) {
      fetchByUrl(parentMenuItem.relative)
        .then(json => setContextNode(json))
        .catch(e => {
          /* eslint-disable no-console */
          console.error(e);
          /* eslint-enable no-console */
        });
    } else {
      setContextNode(null);
    }
    return () => abortFetch();
  }, [parentUrlString, JSON.stringify(location), JSON.stringify(menuTree)]);

  return contextNode;
}

/**
 * Provider for guide configuration.
 */
export function useGuideConfig() {
  const [guideConfig, setGuideConfig] = React.useState({});
  const [fetchByUrl, abortFetch] = useAudioGuideApi();

  const updateGuideConfig = async (target_uuid, componentCSS) => {
    const hardcodedConfig = audioGuideGroups(componentCSS);
    let finalisedConfig = {
      configState: "empty"
    };
    if ((target_uuid || ''.length > 0)) {
      const defaultConfig = hardcodedConfig?.[target_uuid] || {};
      finalisedConfig = { ...defaultConfig, configState: "hardcoded" };
      try {
        const result = await fetchByUrl(`/jsonapi/node/audio_guide/${target_uuid}`);
        const config = result?.data?.attributes?.field_guide_frontend_config || '{}';
        if (config.length > 0) {
          const decoded = JSON.parse(config);
          finalisedConfig = {
            ...defaultConfig,
            ...decoded,
            configState: "content"
          };
        } else {
          /* eslint-disable no-console */
          console.warn(`No guide config found in node ${target_uuid}`);
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      console.warn(`No guide node UUID ${target_uuid} available, uding default config.`);
      /* eslint-enable no-console */
    }
    setGuideConfig(finalisedConfig);
  };

  return [guideConfig, updateGuideConfig, abortFetch];
}

/**
 * Internal string hashing function.
 * @param str The string to hash.
 */
const hashStr = (str = "") => {
  let hash = 0;
  let i;
  let chr;
  if (str.length === 0) {
    return hash.toString();
  }
  for (i = 0; i < str.length; i += 1) {
    chr = str.charCodeAt(i);
    /* eslint-disable no-bitwise */
    hash = (hash << 5) - hash + chr;
    hash |= 0;
    /* eslint-enable no-bitwise */
  }
  return hash.toString();
};

/**
 * Get the hash of a React useEffect dependency.
 * Yes, we're using good old JSON.stringify. It's faster than
 * object-hash, or even just combining the two.
 * We might want to consider something other than a vanilla
 * JSON.stringify because it doesn't handle recursive structures
 * very well.
 * @param {object} obj The effect dependency.
 */
export const hashDep = obj => hashStr(JSON.stringify(obj));

/**
 * Get document interface Event.
 */
export const interfaceEvent = () => {
  const isClient = typeof document === "object";
  if (!isClient) {
    return "";
  }

  const [eventType, setEventType] = React.useState("not-touch");
  const nativeEvent = "touch";

  React.useEffect(() => {
    document.addEventListener(
      "touchstart",
      () => {
        setEventType(nativeEvent);
      },
      true
    );

    return () =>
      document.removeEventListener("touchstart", () => {
        setEventType(nativeEvent);
      });
  }, []);
  return eventType;
};
